'use client';

import { getAvailableCouponsList, getCoupon } from '@/api/coupon';
import { toast } from '@/components/Toast';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { CloseOutlined } from '@zeelool/icon';
import Image from 'next/image';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { CouponRuntimeStatus, HomepageCouponConfig, HomepageCouponProps } from './types';
import { formatCouponAmount, resolveCouponAmount, selectHomepageCoupon } from './utils';

const FIXED_DISPLAY = 1;
const FLOATING_DISPLAY = 2;
const FLOATING_COUPON_ICON_SRC = '/zeelool-next/home-page/coupon-ticket-02.svg';
const couponListRequests = new Map<string, ReturnType<typeof getAvailableCouponsList>>();

function getCouponListDeduped(authKey: string) {
  const pendingRequest = couponListRequests.get(authKey);
  if (pendingRequest) return pendingRequest;

  const request = getAvailableCouponsList();
  couponListRequests.set(authKey, request);
  void request.then(
    () => couponListRequests.delete(authKey),
    () => couponListRequests.delete(authKey),
  );
  return request;
}

function FixedCouponCard({
  coupon,
  amount,
  claiming,
  onClaim,
}: {
  coupon: HomepageCouponConfig;
  amount: string;
  claiming: boolean;
  onClaim: () => void;
}) {
  return (
    <div
      className="relative grid h-[48px] w-full grid-cols-[50px_minmax(0,1fr)_auto] items-center gap-x-[24px] rounded-[6px] px-[8px]"
      style={{ backgroundColor: coupon.coupon_bg_color || '#D9E2FF' }}
      data-testid="homepage-coupon-card"
    >
      <span
        aria-hidden
        data-testid="homepage-coupon-divider-notch"
        className="pointer-events-none absolute left-[61px] top-[-6px] h-[12px] w-[12px] rounded-full bg-white"
      />
      <span
        aria-hidden
        data-testid="homepage-coupon-divider-notch"
        className="pointer-events-none absolute bottom-[-6px] left-[61px] h-[12px] w-[12px] rounded-full bg-white"
      />

      <span
        className="w-full min-w-0 justify-self-center overflow-hidden whitespace-nowrap text-center text-[20px] leading-[24px]"
        data-testid="homepage-coupon-amount"
        style={{
          color: coupon.amount_color || '#080506',
          fontFamily: 'var(--font-bricolage-bold)',
        }}
      >
        {amount}
      </span>

      <span className="min-w-0">
        <span
          className="block truncate text-[14px] leading-[17px]"
          style={{
            color: coupon.title_color || '#080506',
            fontFamily: 'var(--font-bricolage-bold)',
          }}
        >
          {coupon.title}
        </span>
        <span
          className="mt-[4px] block truncate text-[10px] leading-[12px]"
          style={{
            color: coupon.sub_title_color || '#080506',
            fontFamily: 'var(--font-bricolage)',
          }}
        >
          {coupon.sub_title}
        </span>
      </span>

      <button
        type="button"
        className="h-[24px] min-w-[55px] rounded-[40px] px-[12px] text-[12px] leading-[14px] disabled:cursor-wait disabled:opacity-60"
        style={{
          backgroundColor: coupon.button_bg_color || '#463AE8',
          color: coupon.button_text_color || '#FFFFFF',
          fontFamily: 'var(--font-bricolage)',
        }}
        disabled={claiming}
        onClick={onClaim}
      >
        {coupon.button_text}
      </button>
    </div>
  );
}

export default function Coupon({ indexData, moduleIndex = 0, onTrack }: HomepageCouponProps) {
  const t = useTranslations('layout');
  const token = useStore((state) => state.token);
  const currency = useStore((state) => state.currency);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const content = indexData.content;
  const configuredCoupons = useMemo(
    () => (Array.isArray(content?.coupon_list) ? content.coupon_list : []),
    [content?.coupon_list],
  );
  const couponIdsKey = configuredCoupons.map((coupon) => coupon.coupon_id).join(',');
  const displayType =
    Number(content?.display_type) === FLOATING_DISPLAY ? FLOATING_DISPLAY : FIXED_DISPLAY;
  const [runtimeCoupons, setRuntimeCoupons] = useState<CouponRuntimeStatus[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const requestIdRef = useRef(0);

  const loadRuntimeCoupons = useCallback(
    async (forceRefresh = false) => {
      const requestId = ++requestIdRef.current;
      try {
        const response = forceRefresh
          ? await getAvailableCouponsList()
          : await getCouponListDeduped(token || '__guest__');
        if (requestId !== requestIdRef.current) return;
        setRuntimeCoupons(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        logger.error('Failed to fetch homepage coupons:', error);
        setRuntimeCoupons([]);
      } finally {
        if (requestId === requestIdRef.current) setHasLoaded(true);
      }
    },
    [token],
  );

  useWhenTokenReady(() => {
    if (configuredCoupons.length === 0) {
      setRuntimeCoupons([]);
      setHasLoaded(true);
      return;
    }

    setHasLoaded(false);
    void loadRuntimeCoupons();
    return () => {
      requestIdRef.current += 1;
    };
  }, [couponIdsKey, configuredCoupons.length, loadRuntimeCoupons]);

  const visibleCoupon = useMemo(
    () => selectHomepageCoupon(configuredCoupons, runtimeCoupons, Boolean(token)),
    [configuredCoupons, runtimeCoupons, token],
  );
  const visibleRuntimeCoupon = visibleCoupon
    ? runtimeCoupons.find((coupon) => Number(coupon.id) === Number(visibleCoupon.coupon_id))
    : undefined;

  const amount = visibleCoupon
    ? formatCouponAmount(
        resolveCouponAmount(visibleRuntimeCoupon),
        currency?.symbol_prefix || currency?.symbol || '$',
        currency?.symbol_suffix || '',
      )
    : '';

  if (!hasLoaded) {
    return displayType === FIXED_DISPLAY && configuredCoupons.length > 0 ? (
      <div className="h-[60px] bg-white" aria-hidden data-testid="homepage-coupon-placeholder" />
    ) : null;
  }

  if (!visibleCoupon || isClosed) return null;

  const handleClaim = async () => {
    onTrack?.({
      module_index: moduleIndex,
      module_name: indexData.component_id || 'coupon',
      module_id: String(indexData.blocks_advances_id || indexData.id || ''),
      coupon_id_list: String(visibleCoupon.coupon_id),
      coupon_name_list: visibleCoupon.title || '',
    });

    if (!token) {
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'home_coupon',
        situation: '首页优惠券组件',
      });
      return;
    }

    try {
      setClaiming(true);
      const response = await getCoupon(visibleCoupon.coupon_id);
      if (response?.code !== undefined && response.code !== 200) return;
      if (response?.msg) toast.success(response.msg);
      await loadRuntimeCoupons(true);
    } catch (error) {
      logger.error('Failed to claim homepage coupon:', error);
    } finally {
      setClaiming(false);
    }
  };

  if (displayType === FIXED_DISPLAY) {
    return (
      <div className="bg-white px-[12px] pt-[12px]">
        <FixedCouponCard
          coupon={visibleCoupon}
          amount={amount}
          claiming={claiming}
          onClaim={handleClaim}
        />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-x-0 z-[45] mx-auto w-full max-w-[375px] px-[8px]"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      data-testid="homepage-floating-coupon"
    >
      <div
        className="flex h-[36px] items-center gap-[12px] rounded-[4px] py-[6px] pl-[12px] pr-[8px]"
        style={{ backgroundColor: visibleCoupon.coupon_bg_color || '#D9E2FF' }}
        data-testid="homepage-floating-coupon-card"
      >
        <div className="flex min-w-0 flex-1 items-center justify-between">
          <span className="flex min-w-0 items-center gap-[6px]">
            <Image
              aria-hidden
              alt=""
              src={FLOATING_COUPON_ICON_SRC}
              width={16}
              height={16}
              className="h-[16px] w-[16px] shrink-0"
            />
            <span
              className="h-[18px] truncate text-center text-[14px] leading-[18px]"
              style={{
                color: visibleCoupon.title_color || '#282828',
                fontFamily: 'var(--font-bricolage-bold)',
              }}
              data-testid="homepage-floating-coupon-title"
            >
              {visibleCoupon.title}
            </span>
          </span>

          <button
            type="button"
            className="flex h-[24px] min-w-[55px] shrink-0 items-center justify-center rounded-[40px] px-[12px] text-[12px] leading-[14px] disabled:cursor-wait disabled:opacity-60"
            style={{
              backgroundColor: visibleCoupon.button_bg_color || '#463AE8',
              color: visibleCoupon.button_text_color || '#FFFFFF',
              fontFamily: 'var(--font-bricolage)',
            }}
            disabled={claiming}
            onClick={handleClaim}
          >
            {visibleCoupon.button_text}
          </button>
        </div>

        <button
          type="button"
          aria-label={t('closeCoupon')}
          className="flex h-[16px] w-[16px] shrink-0 items-center justify-center"
          onClick={() => setIsClosed(true)}
        >
          <CloseOutlined className="text-[16px] text-[#1a1c1e]" aria-hidden />
        </button>
      </div>
    </div>
  );
}
